/**
 * AlphaX Tuning Simulator | Copyright (C) 2026 AlphaX
 * Licensed under GPL-3.0 (https://www.gnu.org/licenses/gpl-3.0.txt)
 * Subscriptions do not negate this license; derivative works must be open source.
 */
/**
 * AlphaX Tuning Simulator - Virtual ECU Logic
 */

class VirtualECU {
    constructor() {
        this.idleDutyCoolantAxis = [20,  40,  60,  70,  80,  90,  100, 110]; // °C
        this.idleDutyCurve      = [90, 75, 60, 55, 50, 45, 45, 45]; // % of maxIdleTpsPct

        this.ectFuelAxis  = [  20,    40,    60,    70,    80,    90,   100,  110]; // °C
        this.ectFuelCurve = [1.25,  1.15,  1.08,  1.04,  1.02,  1.00, 1.00, 1.00]; // multiplier (1.0 = no correction)
        this.idleTargetRpmAxis  = [20, 40, 60, 70, 80, 90, 100, 110]; // °C — same axis as idleDutyCoolantAxis
        this.idleTargetRpmCurve = [950, 900, 875, 860, 850, 850, 850, 850]; // RPM
        this.maxIdleTpsPct     = 10;   // actual TPS% when duty curve reads 100%
        this.idleRpmUpperLimit = 200;  // RPM above liveIdleTargetRpm to disengage idle control
        this.liveIdleTargetRpm = 850;  // runtime: updated each frame by main.js
        this.revLimit = 7200;
        this.revLimitHysteresis = 200;
        this.revLimitFuelCut = true;
        this.revLimitSparkCut = true;
        this.decelCutEnabled = false;
        this.decelCutDelay = 1.5;
        this.injectorSizeCc = 240;
        this.injDtAxis  = [8,     10,    12,    14,    16   ]; // V
        this.injDtCurve = [1.0, 1.0, 1.0, 1.0, 1.0]; // ms — flat default; tuner must calibrate
        this.injectorDeadTimeMs = 1.0; // runtime value — updated per frame from injDtCurve

        this.coilDwellAxis  = [8,    10,   12,   14,    16  ]; // V
        this.coilDwellCurve = [4.50, 3.50, 3.00, 2.60,  2.30]; // ms
        this.coilDwellMs    = 2.50; // runtime value — updated per frame from coilDwellCurve

        this.ignitionMode      = "sequential_cop";
        // "distributor" | "wasted_spark" | "wasted_spark_individual" | "sequential_cop"

        this.sparkPlugGapIn    = 0.040; // user-configured plug gap in inches; pre-populated from engine spec on load

        this.disabledCylinders = new Set(); // 1-based cylinder numbers permanently disabled (coil melt)
        this.meltedCoils       = new Set(); // "dist" | pair-index | cyl-number depending on mode

        this.idleIgnEnabled = false;
        this.idleIgnP = 0.015;
        this.idleIgnI = 0.0;
        this.idleIgnD = 0.0;
        this.fuelOctane = 91;
        
        this.crankingVe = 50;
        this.crankingDecayTime = 3.0;
        this.maxCrankingRpm = 300;           // RPM the starter holds until engine catches
        this.starterCrankTime = 2.0;         // seconds before engine fires (base crank window)
        this.crankingIdleDutyAxis  = [-10,  10,  30,  60,  90]; // °C (5 cells)
        this.crankingIdleDutyCurve = [180, 140, 100,  70,  50]; // % of maxIdleTpsPct (same rule as idleDutyCurve)

        this.postCrankEnrichAxis  = [-10,  10,  30,  60,  90]; // °C coolant temp axis
        this.postCrankEnrichCurve = [1.40, 1.25, 1.15, 1.05, 1.00]; // VE multiplier (1.0 = no enrichment)
        this.postCrankDecayCycles = 50; // engine cycles (720° each) to decay to 1.0×

        // VVT/VTEC Virtual ECU Settings
        this.camSwitchRpm = 5500;
        this.camSwitchTps = 20; // Minimum TPS to engage high cam
        this.vvlFuelAdder = 0; // Global VE % addition when high cam is active
        this.vvlTimingAdder = 0; // Global ignition timing addition when high cam is active

        // VVT Phaser Simulation
        this.vvtPhaserEnabled = false;
        this.vvtKp = 8.0;  // proportional gain  — primary response speed
        this.vvtKi = 2.0;  // integral gain       — eliminates steady-state error
        this.vvtKd = 0.3;  // derivative gain     — damps overshoot

        this.iatTimingAxis   = [0, 20, 40, 60, 80, 100]; // °C
        this.iatTimingCurve  = [0,  0,  0,  0,  0,   0]; // degrees of retard at each IAT (positive = retard)

        // Knock Control
        this.knockEnabled           = false;
        this.knockAttackDeg         = 2.0;   // degrees removed per knock event
        this.knockMaxRetardDeg      = 10.0;  // max accumulated retard
        this.knockRecoveryDegPerRev = 0.5;   // degrees recovered per engine revolution
        this.knockMinTps            = 25.0;  // % TPS below which knock retard is suppressed
        this.knockMinMap            = 60.0;  // kPa MAP below which knock retard is suppressed

        this.knockThresholdAxis  = [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 5500, 6000, 6500, 7000, 7500, 8000];
        this.knockThresholdCurve = new Array(16).fill(-20); // dB threshold (default -20 dB)

        this.knockCenterFreq  = 7.0;   // kHz — bandpass center for filtered knock detection
        this.knockCalibrated  = false;  // gates knockEnabled checkbox; set true on Confirm Calibration

        // IAT VE Compensation curve: 6-point 1D curve, axis in °C, values in % VE correction
        // Negative = reduce VE (lean protection), Positive = enrich. Default: no correction.
        this.iatVeAxis   = [0, 20, 40, 60, 80, 100]; // °C
        this.iatVeCurve  = [1,  1,  1,  1,  1,   1]; // multiplier (1.0 = no change, 0.9 = -10%, 1.1 = +10%)

        // O2 Sensor Transport Delay & Combustion Noise
        this.o2DelayEnabled = false;

        // Fuel Pressure Model
        this.fuelPressureModelEnabled = false;
        // TODO: unit should follow simulation settings pressure unit (kPa / PSI / bar) once that setting exists
        this.injectorReferencePressureKpa = 300; // differential pressure at which injector cc/min rating was measured
        this.baseFuelPressureKpa = 300.0;        // loaded from engine model on startup (43.5 PSI → 300 kPa)
        this.fuelSystemType = 'return';           // 'return' | 'returnless'
        this.stockFuelPumpLph = 130;              // loaded from engine model on startup
        this.effectiveFuelPressureKpa = 300.0;   // runtime — computed each frame
        this.fuelPumpDemandPct = 0;              // runtime — 0-100%+ (>100 = starved)

        // Smart Alternator
        this.smartAltEnabled = false;
        this.smartAltP       = 25.0;
        this.smartAltI       = 2.0;
        this.smartAltD       = 0.0;
        // Target Voltage table: 5-point sub-axes derived from rpmAxis/mapAxis, scales with Axis Wizard
        this.altVoltRpmAxis  = [500, 2500, 4500, 6000, 8000];
        this.altVoltLoadAxis = [20, 60, 100, 130, 180];
        this.altVoltTable    = Array.from({length: 5}, () => Array(5).fill(14.2));
        this.currentBatteryV = 12.5; // runtime — written per frame, read by physics

        // Boost Control Settings (stored in kPa; UI converts to/from PSI when in imperial mode)
        this.wgSpringKpa = 48.3;           // 7.0 PSI
        this.boostCutKpa = 103.4;          // 15.0 PSI
        this.boostCutHysteresisKpa = 13.8; // 2.0 PSI

        // Closed-Loop Boost Control
        this.clBoostEnabled     = false;
        this.clBoostKp          = 0.8;
        this.clBoostKi          = 0.15;
        this.clBoostKd          = 0.05;
        this.clBoostTpsAxis     = [0, 20, 40, 60, 80, 100];
        this.clBoostRpmAxis     = [1000, 2000, 3000, 4000, 5000, 6500];
        this.clBoostTargetTable = this.createTable(6, 6, () => 0);

        // Supercharger Settings
        this.scPulleyRatio = 2.5;
        this.scBypassThreshold = 15.0; // TPS % threshold to close bypass valve

        // Nitrous Oxide System
        this.nitrousEnabled = false;
        this.nitrousJetSize = 28;             // N2O jet diameter, thousandths of an inch
        this.nitrousCalculatedHp = Math.pow(28, 2) * 0.043; // ~33.7 HP, recalculated on Apply
        this.nitrousActivationMode = 'switch'; // 'switch' | 'ecu'
        this.nitrousShotType = 'dry';          // 'dry' | 'wet' (switch mode only)
        this.nitrousFuelJetSize = 28;          // fuel jet size (wet shot only), thousandths
        this.nitrousMinTps = 80;               // ECU control: minimum TPS to activate
        this.nitrousMinRpm = 2500;             // ECU control: minimum RPM
        this.nitrousMaxRpm = 7000;             // ECU control: maximum RPM
        this.nitrousMaxMapKpa = 150;           // ECU control: max MAP safety cut
        this.nitrousAfrCutLean = 13.5;         // ECU control: AFR safety cut (lean condition)
        this.nitrousVeCompPct = 0;             // ECU control: VE% adder when N2O active
        this.nitrousTimingRetard = 0;          // ECU control: degrees timing retard when N2O active

        this.displayLambda = false; // Toggle for AFR Gas vs Lambda display

        // Closed-Loop Fuel Trim (STFT)
        this.closedLoopEnabled    = false;
        this.clDeadband           = 0.02;   // lambda fraction (0.02 = ±2% of target)
        this.clIdleTimeConstant    = 12;    // seconds; integratorGain = 1/timeConstant
        this.clRunningTimeConstant = 8;
        this.clWotTimeConstant     = 5;
        this.clIdleMin    = -0.25;  this.clIdleMax    = 0.25;   // fraction (±25%)
        this.clRunningMin = -0.25;  this.clRunningMax = 0.25;
        this.clWotMin     = -0.15;  this.clWotMax     = 0.15;

        // Launch Control Settings
        this.lcEnabled = false;
        this.lcRpm = 4500;
        this.lcHysteresis = 100;
        this.lcWindowRpm = 500;
        this.lcCutType = "Fuel"; // Fuel, Ignition, Both
        this.lcTimingAbsolute = -15; // Absolute timing at LC RPM
        this.lcWindowStartTiming = 10; // Absolute timing at start of window
        this.lcFuelAdder = 5; // % enrichment at LC RPM
        this.twoStepSwitchActive = false;

        // Trigger Calibration
        this.triggerAngleOffset = 0;    // ECU-configured trigger offset (0–360°), saved in tune
        this.fixedTimingAngle   = 10;   // ECU commanded firing angle (0–45° BTDC)
        this.timingLightMode    = false; // Live UI state — NOT saved in tune

        this.rpmAxis = [500, 1000, 1500, 2000, 2500, 3000, 3500, 4000, 4500, 5000, 5500, 6000, 6500, 7000, 7500, 8000];
        this.mapAxis = [20, 30, 40, 50, 60, 70, 80, 90, 100, 110, 120, 130, 140, 150, 165, 180];
        this.tpsAxis   = [10, 20, 30, 45, 60, 75, 90, 100]; // 8-point TPS axis for WG duty rows
        this.wgRpmAxis = [500, 1500, 2500, 3500, 4500, 5500, 6500, 7500]; // 8-point RPM axis for WG duty cols

        this.vvtRpmAxis = [500, 1500, 2500, 3500, 4500, 5500, 6500, 7500]; // 8-point
        this.vvtMapAxis = [20,  40,   60,   80,  100,  120,  140,  165];  // 8-point

        this.flexEthanolAxis = [0, 20, 40, 60, 80, 100]; // Ethanol %
        this.flexRpmAxis = [1000, 2500, 4000, 5500, 7000, 8500];
        this.ethanolContent = 0; // Slider value 0-100

        this.veTable = this.createTable(16, 16, (r, c) => {
            const rpm = this.rpmAxis[c];
            const map = this.mapAxis[r];
            const wotVe = 45 + (45 * (1 - Math.abs((rpm - 4000) / 5000)));
            const vacuumDrop = 0.4 + 0.6 * (map / 101.3);
            return Math.round(wotVe * vacuumDrop);
        });

        this.timingTable = this.createTable(16, 16, (r, c) => {
            const rpm = this.rpmAxis[c];
            const map = this.mapAxis[r];
            let base = 10 + (rpm / 1000) * 3;
            base -= (map / 100) * 8; 
            return Math.max(0, Math.round(base));
        });

        this.targetAfrTable = this.createTable(16, 16, (r, c) => {
            const map = this.mapAxis[r];
            let afr = 14.7 - ((map - 20) / 100) * 2.2;
            return Math.max(10.0, Math.round(afr * 10) / 10);
        });

        this.wgDutyTable = this.createTable(8, 8, () => 0);

        // Flex Fuel 2D Maps (6x6)
        this.flexMapAxis = [20, 60, 100, 150, 200, 300];
        this.flexTpsAxis = [0, 20, 40, 60, 80, 100];
        this.flexRpmAxis = [1000, 2500, 4000, 5500, 7000, 8500];

        this.flexVeTable = this.createTable(6, 6, () => 0);
        this.flexAfrTable = this.createTable(6, 6, () => 0);
        this.flexTimingTable = this.createTable(6, 6, () => 0);
        this.flexBoostTable = this.createTable(6, 6, () => 0); 

        // Flex Blend Curves (1D: 0%, 10%, 20%... 100% Ethanol)
        this.ethXAxis = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
        this.flexVeBlendCurve = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
        this.flexAfrBlendCurve = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
        this.flexTimingBlendCurve = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
        this.flexBoostBlendCurve = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];

        this.ethanolContent = 0; // Slider value 0-100

        // Default Intake VVT: Add some mid-range advance
        this.vvtIntakeTable = this.createTable(8, 8, (r, c) => {
            const rpm = this.vvtRpmAxis[c];
            const map = this.vvtMapAxis[r];
            if (rpm >= 2000 && rpm <= 5000 && map >= 50) return 20;
            if (rpm > 5000 && map >= 50) return 10;
            return 0;
        });

        // Default Exhaust VVT: 0
        this.vvtExhaustTable = this.createTable(8, 8, () => 0);

        // Acceleration Enrichment
        this.accelMode      = 'none';  // 'none' | 'ms_adder' | 'map_predict'
        this.accelTime      = 0.5;     // seconds, hard-cutoff event duration
        this.accelDecayRate = 2.0;     // ms/s (ms_adder) or 1/s blend decay (map_predict)

        // MS Adder Table: 5×5 (rows=dTPS %/s, cols=RPM) → additional injection ms
        this.accelMsDtpsAxis = [50, 150, 300, 500, 800];
        this.accelMsRpmAxis  = [500, 1500, 3000, 5000, 7000];
        this.accelMsTable = this.createTable(5, 5, (r, c) => {
            const dNorm = r / 4;
            const rNorm = 1 - c / 4;
            return Math.round(dNorm * rNorm * 4 * 10) / 10;
        });

        // MAP Prediction Table: 5×5 (rows=TPS %, cols=RPM) → predicted MAP kPa
        this.accelMapTpsAxis = [10, 25, 50, 75, 100];
        this.accelMapRpmAxis = [500, 1500, 3000, 5000, 7000];
        this.accelMapTable = this.createTable(5, 5, (r, c) => {
            const tps = this.accelMapTpsAxis[r];
            return Math.round(20 + (tps / 100) * 80);  // 20 kPa idle → 100 kPa WOT
        });

        // Gear-Based Boost Compensations
        this.boostGearAxis = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
        this.boostDutyGearComp = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
        this.boostTargetGearComp = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    }

    createTable(rows, cols, generator) {
        let t = [];
        for(let r=0; r<rows; r++) {
            let row = [];
            for(let c=0; c<cols; c++) row.push(generator(r, c));
            t.push(row);
        }
        return t;
    }


    getVE(rpm, mapKpa) { return Interpolation.interpolate2D(rpm, mapKpa, this.rpmAxis, this.mapAxis, this.veTable); }
    getTiming(rpm, mapKpa) { return Interpolation.interpolate2D(rpm, mapKpa, this.rpmAxis, this.mapAxis, this.timingTable); }
    getTargetAfr(rpm, mapKpa) { return Interpolation.interpolate2D(rpm, mapKpa, this.rpmAxis, this.mapAxis, this.targetAfrTable); }
    getWgDuty(rpm, tps) { return Interpolation.interpolate2D(rpm, tps, this.wgRpmAxis, this.tpsAxis, this.wgDutyTable); }
    getClBoostTarget(rpm, tps) { return Interpolation.interpolate2D(rpm, tps, this.clBoostRpmAxis, this.clBoostTpsAxis, this.clBoostTargetTable); }
    getTargetVvtIntake(rpm, mapKpa) { return Interpolation.interpolate2D(rpm, mapKpa, this.vvtRpmAxis, this.vvtMapAxis, this.vvtIntakeTable); }
    getTargetVvtExhaust(rpm, mapKpa) { return Interpolation.interpolate2D(rpm, mapKpa, this.vvtRpmAxis, this.vvtMapAxis, this.vvtExhaustTable); }
    getGearBoostDutyComp(gear) { return Interpolation.interpolate1D(gear, this.boostGearAxis, this.boostDutyGearComp); }
    getGearBoostTargetComp(gear) { return Interpolation.interpolate1D(gear, this.boostGearAxis, this.boostTargetGearComp); }
    
    getFlexVeAdder(rpm, mapKpa, eth) { 
        const baseVal = Interpolation.interpolate2D(rpm, mapKpa, this.flexRpmAxis, this.flexMapAxis, this.flexVeTable);
        const blend = Interpolation.interpolate1D(eth, this.ethXAxis, this.flexVeBlendCurve) / 100.0;
        return baseVal * blend;
    }
    getFlexTimingAdder(rpm, mapKpa, eth) { 
        const baseVal = Interpolation.interpolate2D(rpm, mapKpa, this.flexRpmAxis, this.flexMapAxis, this.flexTimingTable);
        const blend = Interpolation.interpolate1D(eth, this.ethXAxis, this.flexTimingBlendCurve) / 100.0;
        return baseVal * blend;
    }
    getFlexAfrAdder(rpm, mapKpa, eth) { 
        const baseVal = Interpolation.interpolate2D(rpm, mapKpa, this.flexRpmAxis, this.flexMapAxis, this.flexAfrTable);
        const blend = Interpolation.interpolate1D(eth, this.ethXAxis, this.flexAfrBlendCurve) / 100.0;
        return baseVal * blend;
    }
    getFlexBoostAdder(rpm, tps, eth) {
        const baseVal = Interpolation.interpolate2D(rpm, tps, this.flexRpmAxis, this.flexTpsAxis, this.flexBoostTable);
        const blend = Interpolation.interpolate1D(eth, this.ethXAxis, this.flexBoostBlendCurve) / 100.0;
        return baseVal * blend;
    }

    getAccelMsAdder(rpm, dTps) {
        return Interpolation.interpolate2D(rpm, dTps, this.accelMsRpmAxis, this.accelMsDtpsAxis, this.accelMsTable);
    }
    getAccelPredictedMap(rpm, tps) {
        return Interpolation.interpolate2D(rpm, tps, this.accelMapRpmAxis, this.accelMapTpsAxis, this.accelMapTable);
    }

    // Returns wasted-spark coil pairs: [[cylA, cylB], ...] based on firing order
    // Pairs position i with position i + N/2 (360° apart in the firing order).
    // Example: 1-3-4-2 → pairs [1,4] and [3,2]
    getCoilPairs(firingOrderArr, cylinderCount) {
        const pairs = [];
        const half  = Math.floor(cylinderCount / 2);
        for (let i = 0; i < half; i++) {
            pairs.push([firingOrderArr[i], firingOrderArr[i + half]]);
        }
        if (cylinderCount % 2 !== 0) {
            pairs.push([firingOrderArr[cylinderCount - 1], firingOrderArr[cylinderCount - 1]]); // singleton — treated as COP
        }
        return pairs;
    }

    // Returns available coil recharge time (ms) per firing event for the current ignition mode
    getAvailableRechargeMs(rpm, cylinderCount) {
        if (rpm <= 0) return Infinity;
        switch (this.ignitionMode) {
            case "distributor":
                return 120000 / (rpm * cylinderCount); // one coil fires every 720°/N
            case "wasted_spark":
                return 60000 / rpm;  // each coil fires every 360° crank
            case "sequential_cop":
            default:
                return 120000 / rpm; // each coil fires every 720° crank
        }
    }
}

const ecu = new VirtualECU();
